import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  Users,
  Wrench,
  Send,
  Sparkles,
  TrendingUp,
  TrendingDown,
  AlertOctagon,
  AlertTriangle,
  Info,
  ChevronRight,
  ShieldCheck,
  FileText,
  ReceiptText,
  CalendarClock,
  Package,
  IndianRupee,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_ROLE_LABEL } from "@/components/AdminShell";

// ---------- UI atoms ----------
const KPI = ({ label, value, sub, tone = "steel", Icon, testId }) => {
  const toneMap = {
    steel: "bg-[#a8dadc]/30 text-[#457b9d]",
    red: "bg-[#e63946]/10 text-[#e63946]",
    green: "bg-[#12B76A]/10 text-[#12B76A]",
    orange: "bg-[#F79009]/10 text-[#F79009]",
    blue: "bg-[#457b9d]/15 text-[#457b9d]",
  };
  return (
    <div className="bg-white rounded-xl border border-[#E4E7EC] p-4 h-full" data-testid={testId}>
      {Icon && (
        <div className={`w-9 h-9 rounded-lg grid place-items-center mb-3 ${toneMap[tone] || toneMap.steel}`}>
          <Icon size={17} strokeWidth={2} />
        </div>
      )}
      <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">{label}</div>
      <div className="text-[26px] md:text-[30px] font-bold text-[#1d3557] leading-tight mt-1 font-mono">
        {value}
      </div>
      {sub && <div className="text-[11px] text-[#667085] mt-0.5">{sub}</div>}
    </div>
  );
};

const SectionCard = ({ title, subtitle, right, children, testId }) => (
  <div className="bg-white rounded-xl border border-[#E4E7EC]" data-testid={testId}>
    {(title || right) && (
      <div className="px-5 py-3.5 border-b border-[#F1FAEE] flex items-center justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-semibold text-[#1d3557]">{title}</h3>
          {subtitle && <div className="text-[11px] text-[#667085] mt-0.5">{subtitle}</div>}
        </div>
        {right}
      </div>
    )}
    <div>{children}</div>
  </div>
);

// ---------- Adoption spotlight ----------
const AdoptionSpotlight = ({ adoption, trend }) => {
  const delta = adoption?.delta ?? 0;
  const trendPct = (trend?.weeks || []).map((w) => w.adopted_users);
  const max = Math.max(1, ...trendPct);
  return (
    <div
      className="rounded-xl p-6 md:p-8 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg,#1d3557 0%,#152941 100%)" }}
      data-testid="adoption-spotlight"
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
        <div className="lg:col-span-2">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#e63946]/15 text-[#e63946] text-[10px] font-bold uppercase tracking-[0.14em]">
            <Sparkles size={11} /> Core adoption metric
          </div>
          <h3 className="mt-3 text-white text-[22px] md:text-[26px] font-bold leading-tight tracking-tight">
            Users with 6+ Job Cards This Week
          </h3>
          <div className="mt-4 flex items-baseline gap-3">
            <div className="text-[52px] md:text-[64px] leading-none font-bold text-white font-mono" data-testid="adoption-count">
              {adoption?.adopted_users_this_week ?? "—"}
            </div>
            <div className="text-[#a8dadc] font-mono text-[15px]">
              of {adoption?.adopted_users_this_week != null ? Math.max(0, Math.round((adoption.adopted_users_this_week * 100) / Math.max(adoption.adopted_pct || 1, 1))) : "—"} active advisors
            </div>
          </div>
          <div className="mt-2 flex items-center gap-3">
            <span className="text-[13px] text-[#a8dadc]">
              {adoption?.adopted_pct != null ? `${adoption.adopted_pct}% of active advisors` : ""}
            </span>
            <span
              className={`inline-flex items-center gap-1 text-[12px] font-bold rounded-md px-1.5 py-0.5 ${
                delta > 0 ? "bg-[#12B76A]/15 text-[#12B76A]" : delta < 0 ? "bg-[#e63946]/15 text-[#e63946]" : "bg-white/10 text-[#a8dadc]"
              }`}
              data-testid="adoption-delta"
            >
              {delta > 0 && <TrendingUp size={13} />}
              {delta < 0 && <TrendingDown size={13} />}
              {delta > 0 ? "+" : ""}{delta} vs last week
            </span>
          </div>
        </div>
        {/* mini sparkline of last 4 weeks */}
        <div className="hidden lg:block">
          <div className="text-[10px] uppercase tracking-widest text-[#a8dadc] font-semibold mb-2">4-week trend</div>
          <div className="flex items-end gap-2 h-24" data-testid="adoption-sparkline">
            {(trend?.weeks || []).map((w, i) => {
              const h = Math.max(6, Math.round((w.adopted_users / max) * 90));
              return (
                <div key={w.week_start} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className="w-full rounded-t"
                    style={{ height: h, background: i === (trend.weeks.length - 1) ? "#e63946" : "#a8dadc" }}
                  />
                  <div className="text-[9px] font-mono text-[#a8dadc]">{w.adopted_users}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- Alerts ----------
const Alerts = ({ items }) => {
  const map = {
    urgent: { Icon: AlertOctagon, cls: "text-[#e63946] bg-[#e63946]/10" },
    attention: { Icon: AlertTriangle, cls: "text-[#F79009] bg-[#F79009]/10" },
    normal: { Icon: Info, cls: "text-[#457b9d] bg-[#457b9d]/10" },
  };
  const link = (t) => {
    if (!t) return "#";
    if (t.type === "garage") return `/admin/garages/${t.id}`;
    if (t.type === "enquiry") return `/admin/enquiries/${t.id}`;
    if (t.type === "admin_members") return `/admin/members`;
    return "#";
  };
  return (
    <SectionCard title={`Platform Alerts${items.length ? ` · ${items.length}` : ""}`} testId="admin-alerts">
      {items.length === 0 ? (
        <div className="p-8 text-center text-[13px] text-[#667085]">All quiet on the platform.</div>
      ) : (
        <div className="divide-y divide-[#F1FAEE]">
          {items.map((it, i) => {
            const meta = map[it.severity] || map.normal;
            const { Icon } = meta;
            return (
              <Link
                key={i}
                to={link(it.target)}
                data-testid={`admin-alert-${i}`}
                className="flex items-center gap-3 px-5 py-3 hover:bg-[#F7F9FC] transition-colors"
              >
                <div className={`w-7 h-7 rounded-md grid place-items-center shrink-0 ${meta.cls}`}>
                  <Icon size={14} strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#1d3557] truncate">{it.title}</div>
                  <div className="text-[11px] text-[#667085] truncate">{it.detail}</div>
                </div>
                <ChevronRight size={13} className="text-[#a8dadc]" />
              </Link>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
};

// ---------- Page ----------
export default function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState({ alerts: [] });
  const [trend, setTrend] = useState({ weeks: [] });
  const [pulse, setPulse] = useState(null);
  const [highValue, setHighValue] = useState({ rows: [] });

  useEffect(() => {
    Promise.all([
      api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {}),
      api.get("/admin/dashboard/alerts").then((r) => setAlerts(r.data || { alerts: [] })).catch(() => {}),
      api.get("/admin/dashboard/adoption-trend").then((r) => setTrend(r.data || { weeks: [] })).catch(() => {}),
      api.get("/admin/ops/activity-pulse").then((r) => setPulse(r.data)).catch(() => {}),
      api.get("/admin/ops/high-value", { params: { limit: 5, threshold: 100000 } }).then((r) => setHighValue(r.data || { rows: [] })).catch(() => {}),
    ]);
  }, []);

  const roleLabel = ADMIN_ROLE_LABEL[user?.admin_role] || "Admin";
  const totals = data?.totals || {};
  const adoption = data?.adoption || {};
  const workload = data?.workload || {};
  const growth = data?.growth || {};
  const p = pulse || {};

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">
            SparesHub · Command Deck
          </div>
          <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557]" data-testid="admin-dashboard-title">
            Welcome, {(user?.name || "").split(" ")[0] || "Admin"}
          </h1>
          <p className="text-[13px] text-[#667085] mt-0.5">{roleLabel} · monitoring the Fender platform.</p>
        </div>
        <div
          data-testid="admin-security-banner"
          className="inline-flex items-center gap-2 text-[11px] font-semibold text-[#12B76A] bg-[#12B76A]/10 border border-[#12B76A]/25 rounded-md px-3 py-1.5"
        >
          <ShieldCheck size={14} /> Audited session
        </div>
      </div>

      {/* Adoption spotlight */}
      <AdoptionSpotlight adoption={adoption} trend={trend} />

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI testId="kpi-garages" label="Registered garages" value={totals.registered_garages ?? 0} Icon={Building2} sub={`${totals.active_garages ?? 0} active last 30d`} />
        <KPI testId="kpi-users" label="Total users" value={totals.total_users ?? 0} Icon={Users} tone="blue" sub={`+${growth.new_users_this_week ?? 0} this week`} />
        <KPI testId="kpi-jcs" label="Job cards this week" value={workload.job_cards_this_week ?? 0} Icon={Wrench} tone="red" sub={`${workload.job_cards_prev_week ?? 0} last week`} />
        <KPI testId="kpi-enq" label="Enquiries this week" value={workload.enquiries_new_this_week ?? 0} Icon={Send} tone="orange" sub={`${workload.enquiries_open ?? 0} open now`} />
      </div>

      {/* Bottom grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <SectionCard
            title="Activity Snapshot"
            subtitle="This week across the platform"
            testId="activity-snapshot"
          >
            <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
              <SnapItem label="Job cards" value={p.job_cards_this_week ?? workload.job_cards_this_week ?? 0} sub={`${p.job_cards_today ?? 0} today`} />
              <SnapItem label="Estimates" value={p.estimates_this_week ?? 0} />
              <SnapItem label="Invoices" value={p.invoices_this_week ?? 0} sub={p.billed_this_week != null ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(p.billed_this_week) : ""} />
              <SnapItem label="Appointments today" value={p.appointments_today ?? 0} />
              <SnapItem label="New garages" value={growth.new_garages_this_week ?? 0} />
              <SnapItem label="New users" value={growth.new_users_this_week ?? 0} />
              <SnapItem label="Low stock parts" value={p.low_stock_parts ?? 0} tone={p.low_stock_parts > 0 ? "warn" : "normal"} />
              <SnapItem label="Out of stock" value={p.out_of_stock_parts ?? 0} tone={p.out_of_stock_parts > 0 ? "danger" : "normal"} />
            </div>
          </SectionCard>
          <div className="mt-4">
            <HighValueOpportunities rows={highValue.rows || []} />
          </div>
          <div className="mt-4">
            <QuickLinks />
          </div>
        </div>
        <Alerts items={alerts.alerts || []} />
      </div>
    </div>
  );
}

const HighValueOpportunities = ({ rows }) => {
  const inr = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(v || 0));
  return (
    <SectionCard
      title="High Value Opportunities"
      subtitle="Live Job Cards + Estimates ≥ ₹1,00,000"
      right={<IndianRupee size={14} className="text-[#e63946]" />}
      testId="high-value-panel"
    >
      {rows.length === 0 && (
        <div className="p-6 text-center text-[13px] text-[#667085]" data-testid="high-value-empty">
          No high-value records right now — everything under ₹1L.
        </div>
      )}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-[12px]">
            <thead className="bg-[#F7F9FC] text-left text-[#667085] uppercase text-[10px] tracking-widest">
              <tr>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Garage</th>
                <th className="px-4 py-2">Vehicle</th>
                <th className="px-4 py-2 text-right">Value</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1FAEE]">
              {rows.map((r) => {
                const isJC = r.type === "job_card";
                const dest = isJC ? `/admin/job-cards/${r.id}` : `/admin/estimates/${r.id}`;
                return (
                  <tr key={`${r.type}-${r.id}`} data-testid={`high-value-row-${r.id}`} className="hover:bg-[#F7F9FC] cursor-pointer" onClick={() => window.location.assign(dest)}>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider ${isJC ? "bg-[#457b9d]/15 text-[#1d3557] border-[#457b9d]/30" : "bg-[#a8dadc]/25 text-[#1d3557] border-[#a8dadc]/60"}`}>
                        {isJC ? "Job Card" : "Estimate"}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-semibold text-[#1d3557]">{r.garage_name}</td>
                    <td className="px-4 py-2 text-[#1d3557]">
                      {r.vehicle_reg || "—"}
                      {r.vehicle_model && <div className="text-[10px] text-[#667085]">{r.vehicle_model}</div>}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="font-mono font-bold text-[#1d3557]">{inr(r.value)}</div>
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase tracking-wider bg-[#e63946]/8 text-[#e63946] border-[#e63946]/30 mt-0.5">High Value</span>
                    </td>
                    <td className="px-4 py-2 text-[#457b9d] uppercase text-[10px] tracking-wider font-semibold">{(r.status || "").replace(/_/g, " ")}</td>
                    <td className="px-4 py-2 text-[#667085] text-[11px]">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}</td>
                    <td className="px-4 py-2 text-right"><ChevronRight size={13} className="text-[#a8dadc]" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
};

const SnapItem = ({ label, value, sub, tone = "normal" }) => {
  const cls = tone === "danger" ? "text-[#e63946]" : tone === "warn" ? "text-[#B54708]" : "text-[#1d3557]";
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">{label}</div>
      <div className={`text-[22px] font-bold font-mono mt-0.5 ${cls}`}>{value}</div>
      {sub && <div className="text-[11px] text-[#667085] mt-0.5">{sub}</div>}
    </div>
  );
};

const QuickLinks = () => (
  <SectionCard title="Jump to" testId="admin-quick-links">
    <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-2">
      {[
        { to: "/admin/job-cards", label: "Job Cards", Icon: Wrench },
        { to: "/admin/estimates", label: "Estimates", Icon: FileText },
        { to: "/admin/invoices", label: "Invoices", Icon: ReceiptText },
        { to: "/admin/inventory", label: "Inventory", Icon: Package },
        { to: "/admin/appointments", label: "Appointments", Icon: CalendarClock },
        { to: "/admin/garages", label: "Garages", Icon: Building2 },
        { to: "/admin/users", label: "Users", Icon: Users },
        { to: "/admin/audit-log", label: "Audit Log", Icon: ShieldCheck },
      ].map(({ to, label, Icon }) => (
        <Link
          key={to}
          to={to}
          data-testid={`admin-jump-${label.toLowerCase().replace(/\s+/g, "-")}`}
          className="h-16 rounded-lg border border-[#E4E7EC] bg-white hover:border-[#457b9d] hover:bg-[#F1FAEE] transition-colors flex items-center gap-3 px-3"
        >
          <div className="w-8 h-8 rounded-md bg-[#F1FAEE] grid place-items-center text-[#457b9d]">
            <Icon size={15} />
          </div>
          <div className="text-[13px] font-semibold text-[#1d3557]">{label}</div>
          <ChevronRight size={13} className="text-[#a8dadc] ml-auto" />
        </Link>
      ))}
    </div>
  </SectionCard>
);
